import { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import api, { formatINR, formatINRDecimal } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import ProgressTracker from "@/components/ProgressTracker";
import SourceBadge from "@/components/SourceBadge";
import { useAuth } from "@/contexts/AuthContext";
import { Upload, FileText, Gauge, Banknote, ScrollText, History, CheckCircle2, ArrowDown, AlertTriangle, ShieldAlert, Info, Sparkles } from "lucide-react";
import { toast } from "sonner";
import CreditDecisionSummary from "@/components/application/CreditDecisionSummary";
import CashFlowTab from "@/components/application/CashFlowTab";
import RiskScoreCard from "@/components/application/RiskScoreCard";

const HEALTH_GRADES = ["A", "B", "C", "D", "Reject"];
const DOC_CATEGORIES = ["PAN", "Aadhaar", "Address proof", "Bank statement", "Salary slip", "ITR / GST", "Photo", "RC", "Insurance", "Pollution certificate", "Vehicle photos", "Dealer invoice"];

// Risk-scoring policy thresholds — keep in sync with backend `business_logic.DEFAULT_POLICY`.
const RISK_THRESHOLDS = {
  CIBIL_MIN: 650,
  CIBIL_SUBPRIME: 700,
  FOIR_HIGH_PCT: 55,
  FOIR_WATCH_PCT: 45,
  LTV_HIGH_PCT: 80,
  VEHICLE_AGE_MAX: 10,
  VEHICLE_AGE_WATCH: 7,
  KM_MAX: 150000,
  KM_WATCH: 100000,
  REPAIR_TO_VALUE_PCT: 0.10,
  CONFIDENCE_LOW: 50,
  ENQUIRIES_6M_HIGH: 5,
};

export default function ApplicationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const pdfInputRef = useRef();
  const docInputRef = useRef();

  const load = async () => {
    try {
      const { data } = await api.get(`/applications/${id}`);
      setApp(data);
    } catch (e) {
      console.error("Could not load application:", e);
      toast.error("Could not load application");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stable [busy, setBusy] tuple so child components don't see a new prop reference each render.
  const busyState = useMemo(() => [busy, setBusy], [busy]);

  if (loading) return <div className="text-slate-500">Loading…</div>;
  if (!app) return <div className="text-slate-500">Application not found.</div>;

  const isStaff = ["admin", "operations", "underwriter", "super_admin"].includes(user?.role);
  const canDecision = ["underwriter", "admin", "super_admin"].includes(user?.role);

  const uploadPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/applications/${id}/cardr/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("CarDr PDF uploaded — extracting…");
      // Auto-fire AI extraction immediately so the user doesn't have to click a second button
      try {
        await api.post(`/applications/${id}/cardr/ai-extract`);
        toast.success("✨ AI extraction complete — review the fields below");
      } catch (ex) {
        toast.error(ex.response?.data?.detail || "AI extraction failed — you can retry with the ✨ AI Extract button");
      }
      load();
    } catch {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
      if (pdfInputRef.current) pdfInputRef.current.value = "";
    }
  };

  const runValuation = async () => {
    setBusy(true);
    try { await api.post(`/applications/${id}/valuation/run`); toast.success("Valuation computed"); load(); } catch { toast.error("Failed"); } finally { setBusy(false); }
  };
  const runEligibility = async () => {
    setBusy(true);
    try { await api.post(`/applications/${id}/eligibility/run`); toast.success("Eligibility computed"); load(); } catch { toast.error("Failed"); } finally { setBusy(false); }
  };
  const generateSanction = async () => {
    setBusy(true);
    try { await api.post(`/applications/${id}/sanction/generate`); toast.success("Sanction letter generated"); load(); } catch { toast.error("Failed"); } finally { setBusy(false); }
  };
  const disburse = async () => {
    setBusy(true);
    try { await api.post(`/applications/${id}/disburse`); toast.success("Disbursed & EMI schedule generated"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const uploadDoc = async (category, file) => {
    setBusy(true);
    const fd = new FormData(); fd.append("file", file); fd.append("category", category);
    try { await api.post(`/applications/${id}/documents/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Document uploaded"); load(); } catch { toast.error("Failed"); } finally { setBusy(false); }
  };

  return (
    <div data-testid="application-detail">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-mono">{app.application_id}</div>
          <h1 className="text-2xl font-semibold text-slate-900 mt-1">{app.borrower?.full_name || "Borrower"}</h1>
          <div className="text-sm text-slate-600">{app.vehicle?.make} {app.vehicle?.model} · {app.vehicle?.year} · {app.borrower?.city}</div>
        </div>
        <div className="flex items-center gap-3">
          <SourceBadge source={app.source} />
          <StatusBadge status={app.status} />
          {app.sanction?.letter_no && (
            <Button variant="outline" size="sm" onClick={() => navigate(`/sanction/${id}`)} data-testid="view-sanction-btn">View sanction letter</Button>
          )}
          {app.agreement?.agreement_no ? (
            <Button variant="outline" size="sm" onClick={() => navigate(`/agreement/${id}`)} data-testid="view-agreement-btn">Agreement & KFS</Button>
          ) : (app.sanction?.letter_no && canDecision && (
            <Button size="sm" disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="generate-agreement-btn"
              onClick={async () => {
                setBusy(true);
                try {
                  await api.post(`/applications/${id}/agreement/generate`);
                  toast.success("Loan agreement & KFS generated");
                  load();
                } catch (e) { toast.error(e.response?.data?.detail || "Failed to generate agreement"); }
                finally { setBusy(false); }
              }}>Generate Agreement & KFS</Button>
          ))}
        </div>
      </div>

      {/* Visual progress tracker */}
      <Card className="p-5 border-slate-200 mb-4" data-testid="progress-card">
        <ProgressTracker status={app.status} />
      </Card>

      {/* Credit decision summary (staff) */}
      {isStaff && <CreditDecisionSummary app={app} />}

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="cardr" data-testid="tab-cardr">CarDr Report</TabsTrigger>
          <TabsTrigger value="valuation" data-testid="tab-valuation">Valuation</TabsTrigger>
          <TabsTrigger value="credit" data-testid="tab-credit">Credit & Offer</TabsTrigger>
          {isStaff && <TabsTrigger value="cashflow" data-testid="tab-cashflow">Cash Flow</TabsTrigger>}
          {canDecision && <TabsTrigger value="decision" data-testid="tab-decision">Underwriter</TabsTrigger>}
          <TabsTrigger value="parties" data-testid="tab-parties">Parties</TabsTrigger>
          <TabsTrigger value="bureau" data-testid="tab-bureau">Bureau & AA</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="servicing" data-testid="tab-servicing">Servicing</TabsTrigger>
          <TabsTrigger value="emi" data-testid="tab-emi">EMI Schedule</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit</TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <InfoCard title="Borrower" data={app.borrower} />
            <InfoCard title="Vehicle" data={app.vehicle} />
            <InfoCard title="Loan" data={app.loan} />
          </div>
          {app.loan_offer && (
            <Card className="p-5 border-teal-200 bg-teal-50 mt-4" data-testid="offer-summary">
              <div className="text-xs uppercase tracking-wider text-teal-700 font-semibold mb-2">Draft / Current Offer</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <KV label="Loan amount" value={formatINR(app.loan_offer.loan_amount)} />
                <KV label="ROI" value={app.loan_offer.roi ? `${app.loan_offer.roi}%` : "—"} />
                <KV label="Tenure" value={`${app.loan_offer.tenure_months || "—"} mo`} />
                <KV label="EMI" value={formatINR(app.loan_offer.emi)} />
                <KV label="Processing fee" value={formatINR(app.loan_offer.processing_fee)} />
                <KV label="Down payment" value={formatINR(app.loan_offer.down_payment)} />
                <KV label="Offer status" value={app.loan_offer.status} />
                <KV label="Expires" value={app.loan_offer.expiry_date} />
              </div>
            </Card>
          )}
        </TabsContent>

        {/* CARDR */}
        <TabsContent value="cardr">
          <Card className="p-5 border-slate-200">
            <div className="flex justify-between items-center mb-4">
              <div>
                <div className="text-lg font-semibold text-slate-900">CarDr Diagnostic Report</div>
                <div className="text-sm text-slate-600">Upload the PDF and confirm extracted fields below.</div>
              </div>
              <div className="flex items-center gap-2">
                <input ref={pdfInputRef} type="file" accept="application/pdf" hidden onChange={uploadPdf} data-testid="cardr-pdf-input" />
                <Button onClick={() => pdfInputRef.current?.click()} disabled={busy} data-testid="cardr-upload-btn" className="bg-slate-900 hover:bg-slate-800"><Upload size={16} className="mr-1" /> Upload PDF</Button>
                {app.cardr_pdf?.filename && (
                  <>
                    <a className="text-sm text-teal-700 font-medium flex items-center gap-1" href={`${api.defaults.baseURL}/applications/${id}/cardr/pdf`} target="_blank" rel="noreferrer" data-testid="cardr-pdf-link">
                      <FileText size={14} /> {app.cardr_pdf.filename}
                    </a>
                    <Button onClick={async () => {
                      setBusy(true);
                      try {
                        await api.post(`/applications/${id}/cardr/ai-extract`);
                        toast.success("AI extraction complete — review below");
                        load();
                      } catch (e) { toast.error(e.response?.data?.detail || "AI extraction failed"); }
                      finally { setBusy(false); }
                    }} disabled={busy} className="bg-teal-600 hover:bg-teal-700" data-testid="cardr-ai-extract-btn">✨ AI Extract</Button>
                  </>
                )}
              </div>
            </div>
            <CurrencyConversionBanner cardr={app.cardr} />
            <CarDrExtractForm app={app} onSaved={load} busyState={busyState} />
          </Card>
        </TabsContent>

        {/* VALUATION */}
        <TabsContent value="valuation">
          <MarketEstimateCard app={app} />
          <Card className="p-5 border-slate-200 mt-4">
            <div className="flex justify-between items-center mb-4">
              <div>
                <div className="text-lg font-semibold text-slate-900 flex items-center gap-2"><Gauge size={18} /> Vehicle Valuation</div>
                <div className="text-sm text-slate-600">Computed using CarDr-adjusted lendable value formula.</div>
              </div>
              {isStaff && <Button onClick={runValuation} disabled={busy} className="bg-teal-600 hover:bg-teal-700" data-testid="run-valuation-btn">Run valuation</Button>}
            </div>
            {!app.valuation ? <div className="text-sm text-slate-500">No valuation yet. Run valuation after confirming CarDr report.</div> : (
              <ValuationBreakdown v={app.valuation} />
            )}
          </Card>
        </TabsContent>

        {/* CREDIT & OFFER */}
        <TabsContent value="credit">
          <Card className="p-5 border-slate-200">
            <div className="flex justify-between items-center mb-4">
              <div>
                <div className="text-lg font-semibold text-slate-900 flex items-center gap-2"><Banknote size={18} /> Credit Eligibility & Loan Offer</div>
                <div className="text-sm text-slate-600">FOIR / LTV / EMI computed by Red Fort Capital rules engine.</div>
              </div>
              {isStaff && <Button onClick={runEligibility} disabled={busy} className="bg-teal-600 hover:bg-teal-700" data-testid="run-eligibility-btn">Run eligibility</Button>}
            </div>
            {!app.credit_decision ? <div className="text-sm text-slate-500">No credit decision yet. Run valuation and eligibility.</div> : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <KV label="Decision" value={app.credit_decision.decision} highlight />
                <KV label="Approved amount" value={formatINR(app.credit_decision.approved_amount)} highlight />
                <KV label="ROI" value={`${app.credit_decision.roi}%`} />
                <KV label="Tenure" value={`${app.credit_decision.tenure_months} mo`} />
                <KV label="EMI" value={formatINR(app.credit_decision.emi)} />
                <KV label="FOIR" value={`${Math.round((app.credit_decision.foir || 0) * 1000) / 10}%`} />
                <KV label="LTV" value={`${Math.round((app.credit_decision.ltv || 0) * 1000) / 10}%`} />
                <KV label="Processing fee" value={formatINR(app.credit_decision.processing_fee)} />
                <KV label="Down payment" value={formatINR(app.credit_decision.required_down_payment)} />
                {app.credit_decision.reason_codes?.length > 0 && (
                  <div className="md:col-span-3 p-3 rounded-md bg-blue-50 border border-blue-200 text-blue-900 text-sm">
                    <div className="font-semibold mb-1">Reasons</div>
                    <ul className="list-disc pl-5 space-y-1">{app.credit_decision.reason_codes.map((r, i) => <li key={i}>{r}</li>)}</ul>
                  </div>
                )}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* CASH FLOW */}
        {isStaff && (
          <TabsContent value="cashflow">
            <CashFlowTab appId={id} app={app} onRefresh={load} busyState={busyState} />
          </TabsContent>
        )}

        {/* DECISION */}
        {canDecision && (
          <TabsContent value="decision">
            <div className="space-y-4">
              <RiskScoreCard appId={id} app={app} onRefresh={load} busyState={busyState} />
              <RiskFlagPanel app={app} />
              <UnderwriterDecisionForm app={app} onSaved={load} busyState={busyState} onGenerateSanction={generateSanction} onDisburse={disburse} />
            </div>
          </TabsContent>
        )}

        {/* PARTIES (Co-borrower / Guarantor) */}
        <TabsContent value="parties">
          <PartiesTab app={app} onSaved={load} busyState={busyState} />
        </TabsContent>

        {/* BUREAU & AA */}
        <TabsContent value="bureau">
          <BureauAATab app={app} onSaved={load} busyState={busyState} />
        </TabsContent>

        {/* DOCUMENTS */}
        <TabsContent value="documents">
          <Card className="p-5 border-slate-200">
            <div className="flex justify-between items-center mb-4">
              <div className="text-lg font-semibold text-slate-900">Documents</div>
              <DocUploader onUpload={uploadDoc} disabled={busy} />
            </div>
            {(app.documents || []).length === 0 ? <div className="text-sm text-slate-500">No documents uploaded yet.</div> : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                  <tr><th className="text-left px-4 py-2">Category</th><th className="text-left px-4 py-2">File</th><th className="text-left px-4 py-2">Status</th><th className="text-left px-4 py-2">Uploaded</th></tr>
                </thead>
                <tbody>
                  {app.documents.map((d, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-4 py-2">{d.category}</td>
                      <td className="px-4 py-2"><a className="text-teal-700 font-medium" href={`${api.defaults.baseURL}/applications/${id}/documents/${d.id}/download`} target="_blank" rel="noreferrer">{d.filename}</a></td>
                      <td className="px-4 py-2">{d.status}</td>
                      <td className="px-4 py-2 text-xs text-slate-500">{d.uploaded_at}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </TabsContent>

        {/* SERVICING (eNACH / GPS / RTO + Co-lending + Sell-down) */}
        <TabsContent value="servicing">
          <ServicingTab app={app} onSaved={load} busyState={busyState} />
        </TabsContent>

        {/* EMI */}
        <TabsContent value="emi">
          <EmiTab app={app} />
        </TabsContent>

        {/* AUDIT */}
        <TabsContent value="audit">
          <Card className="p-5 border-slate-200">
            <div className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2"><History size={18} /> Audit trail</div>
            <div className="space-y-2">
              {(app.audit || []).slice().reverse().map((a, i) => (
                <div key={i} className="text-sm border border-slate-100 rounded-md p-3 flex items-start gap-3" data-testid={`audit-row-${i}`}>
                  <CheckCircle2 size={16} className="text-teal-600 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-medium text-slate-900">{a.action}</div>
                    <div className="text-xs text-slate-500">{a.at} · {a.by_name} ({a.by_role})</div>
                    {a.details && Object.keys(a.details).length > 0 && (
                      <div className="text-xs text-slate-600 font-mono mt-1">{JSON.stringify(a.details)}</div>
                    )}
                  </div>
                </div>
              ))}
              {(!app.audit || app.audit.length === 0) && <div className="text-sm text-slate-500">No audit events yet.</div>}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function KV({ label, value, highlight }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-0.5 font-mono ${highlight ? "text-teal-700 font-semibold" : "text-slate-900"}`}>{value ?? "—"}</div>
    </div>
  );
}

function InfoCard({ title, data }) {
  return (
    <Card className="p-5 border-slate-200">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">{title}</div>
      <div className="space-y-1.5 text-sm">
        {Object.entries(data || {}).map(([k, v]) => v != null && v !== "" && (
          <div key={k} className="flex justify-between gap-2">
            <div className="text-slate-500 capitalize">{k.replace(/_/g, " ")}</div>
            <div className="text-slate-900 font-mono text-right">{typeof v === "object" ? JSON.stringify(v) : String(v)}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function CarDrExtractForm({ app, onSaved, busyState }) {
  const [busy, setBusy] = busyState;
  const [form, setForm] = useState(() => ({
    vin: app.cardr?.vin || app.vehicle?.vin || "",
    registration_number: app.cardr?.registration_number || app.vehicle?.registration_number || "",
    year: app.cardr?.year || app.vehicle?.year || "",
    make: app.cardr?.make || app.vehicle?.make || "",
    model_name: app.cardr?.model_name || app.vehicle?.model || "",
    variant: app.cardr?.variant || app.vehicle?.variant || "",
    fuel_type: app.cardr?.fuel_type || app.vehicle?.fuel_type || "",
    odometer: app.cardr?.odometer || app.vehicle?.km_driven || 0,
    modules_scanned: app.cardr?.modules_scanned || 0,
    current_dtc_count: app.cardr?.current_dtc_count || 0,
    pending_dtc_count: app.cardr?.pending_dtc_count || 0,
    historic_dtc_count: app.cardr?.historic_dtc_count || 0,
    permanent_dtc_count: app.cardr?.permanent_dtc_count || 0,
    code_reset_detected: !!app.cardr?.code_reset_detected,
    emissions_readiness: app.cardr?.emissions_readiness || "Ready",
    repair_estimate: app.cardr?.repair_estimate || 0,
    parts_estimate: app.cardr?.parts_estimate || 0,
    labor_estimate: app.cardr?.labor_estimate || 0,
    vehicle_health_score: app.cardr?.vehicle_health_score || 80,
    diagnostic_confidence: app.cardr?.diagnostic_confidence || 90,
    mileage_mismatch_flag: !!app.cardr?.mileage_mismatch_flag,
    vin_mismatch_flag: !!app.cardr?.vin_mismatch_flag,
    recommended_inspection_flag: !!app.cardr?.recommended_inspection_flag,
    underwriting_warning_flag: !!app.cardr?.underwriting_warning_flag,
    health_grade: app.cardr?.health_grade || "B",
  }));
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      ["year", "odometer", "modules_scanned", "current_dtc_count", "pending_dtc_count", "historic_dtc_count", "permanent_dtc_count", "repair_estimate", "parts_estimate", "labor_estimate", "vehicle_health_score", "diagnostic_confidence"].forEach(k => { payload[k] = Number(payload[k]) || 0; });
      await api.post(`/applications/${app.id}/cardr/extract`, payload);
      toast.success("CarDr extract saved");
      onSaved();
    } catch (e) { toast.error("Failed"); } finally { setBusy(false); }
  };

  const F = (props) => (
    <div>
      <Label htmlFor={props.id}>{props.label}</Label>
      <Input id={props.id} data-testid={`cardr-${props.id}`} value={form[props.id] ?? ""} type={props.type || "text"} onChange={e => set(props.id, props.type === "number" ? Number(e.target.value) : e.target.value)} />
    </div>
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <F id="vin" label="VIN" />
      <F id="registration_number" label="Registration No" />
      <F id="year" label="Year" type="number" />
      <F id="make" label="Make" />
      <F id="model_name" label="Model" />
      <F id="variant" label="Variant" />
      <F id="fuel_type" label="Fuel" />
      <F id="odometer" label="Odometer" type="number" />
      <F id="modules_scanned" label="Modules scanned" type="number" />
      <F id="current_dtc_count" label="Current DTCs" type="number" />
      <F id="pending_dtc_count" label="Pending DTCs" type="number" />
      <F id="historic_dtc_count" label="Historic DTCs" type="number" />
      <F id="permanent_dtc_count" label="Permanent DTCs" type="number" />
      <F id="repair_estimate" label="Repair estimate (₹)" type="number" />
      <F id="parts_estimate" label="Parts estimate (₹)" type="number" />
      <F id="labor_estimate" label="Labor estimate (₹)" type="number" />
      <F id="vehicle_health_score" label="Health score" type="number" />
      <F id="diagnostic_confidence" label="Diagnostic confidence" type="number" />
      <F id="emissions_readiness" label="Emissions readiness" />

      <div>
        <Label>Health Grade</Label>
        <Select value={form.health_grade} onValueChange={v => set("health_grade", v)}>
          <SelectTrigger data-testid="cardr-health-grade"><SelectValue /></SelectTrigger>
          <SelectContent>{HEALTH_GRADES.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="md:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
        <CheckRow id="code_reset_detected" form={form} set={set} label="Code reset detected" />
        <CheckRow id="mileage_mismatch_flag" form={form} set={set} label="Mileage mismatch" />
        <CheckRow id="vin_mismatch_flag" form={form} set={set} label="VIN mismatch" />
        <CheckRow id="recommended_inspection_flag" form={form} set={set} label="Inspection recommended" />
        <CheckRow id="underwriting_warning_flag" form={form} set={set} label="Underwriting warning" />
      </div>

      <div className="md:col-span-3 flex justify-end mt-2">
        <Button onClick={save} disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="save-cardr-btn">Save CarDr extract</Button>
      </div>
    </div>
  );
}

function CheckRow({ id, form, set, label }) {
  return (
    <label className="flex items-center gap-2 text-sm" data-testid={`cardr-flag-${id}`}>
      <Checkbox checked={!!form[id]} onCheckedChange={v => set(id, !!v)} />
      <span>{label}</span>
    </label>
  );
}

function DocUploader({ onUpload, disabled }) {
  const [cat, setCat] = useState(DOC_CATEGORIES[0]);
  const inputRef = useRef();
  return (
    <div className="flex items-center gap-2">
      <Select value={cat} onValueChange={setCat}>
        <SelectTrigger className="w-48" data-testid="doc-category-select"><SelectValue /></SelectTrigger>
        <SelectContent>{DOC_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
      </Select>
      <input ref={inputRef} type="file" hidden onChange={e => { const f = e.target.files?.[0]; if (f) onUpload(cat, f); e.target.value=""; }} data-testid="doc-file-input" />
      <Button variant="outline" onClick={() => inputRef.current?.click()} disabled={disabled} data-testid="doc-upload-btn"><Upload size={16} className="mr-1" /> Upload</Button>
    </div>
  );
}

function UnderwriterDecisionForm({ app, onSaved, busyState, onGenerateSanction, onDisburse }) {
  const [busy, setBusy] = busyState;
  const cd = app.credit_decision || {};
  const [d, setD] = useState({
    decision: "approve",
    approved_amount: cd.approved_amount || app.loan_offer?.loan_amount || 0,
    roi: cd.roi || 12.5,
    tenure_months: cd.tenure_months || 60,
    processing_fee: cd.processing_fee || 0,
    down_payment_required: cd.required_down_payment || 0,
    requires_coborrower: false,
    requires_guarantor: false,
    requires_gps: false,
    notes: "",
    conditions: [],
    reason_codes: [],
  });
  const set = (k, v) => setD(p => ({ ...p, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/applications/${app.id}/decision`, { ...d, approved_amount: Number(d.approved_amount), roi: Number(d.roi), tenure_months: Number(d.tenure_months), processing_fee: Number(d.processing_fee), down_payment_required: Number(d.down_payment_required) });
      toast.success(`Decision saved: ${d.decision}`);
      onSaved();
    } catch (e) { toast.error("Failed"); } finally { setBusy(false); }
  };

  return (
    <Card className="p-5 border-slate-200" data-testid="underwriter-decision-form">
      <div className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2"><ScrollText size={18} /> Underwriter Decision</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label>Decision</Label>
          <Select value={d.decision} onValueChange={v => set("decision", v)}>
            <SelectTrigger data-testid="uw-decision-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="approve">Approve</SelectItem>
              <SelectItem value="reject">Reject</SelectItem>
              <SelectItem value="refer">Refer / Additional docs</SelectItem>
              <SelectItem value="counteroffer">Counteroffer</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Field label="Approved amount (₹)" type="number" value={d.approved_amount} onChange={v => set("approved_amount", v)} id="uw-amount" />
        <Field label="ROI %" type="number" value={d.roi} onChange={v => set("roi", v)} id="uw-roi" />
        <Field label="Tenure (months)" type="number" value={d.tenure_months} onChange={v => set("tenure_months", v)} id="uw-tenure" />
        <Field label="Processing fee (₹)" type="number" value={d.processing_fee} onChange={v => set("processing_fee", v)} id="uw-fee" />
        <Field label="Down payment required (₹)" type="number" value={d.down_payment_required} onChange={v => set("down_payment_required", v)} id="uw-dp" />
        <CheckRow id="requires_coborrower" form={d} set={set} label="Require co-borrower" />
        <CheckRow id="requires_guarantor" form={d} set={set} label="Require guarantor" />
        <CheckRow id="requires_gps" form={d} set={set} label="Require GPS device" />
        <div className="md:col-span-3">
          <Label>Notes</Label>
          <Textarea data-testid="uw-notes" rows={3} value={d.notes} onChange={e => set("notes", e.target.value)} />
        </div>
      </div>
      <div className="mt-5 flex flex-wrap gap-2 justify-end">
        <Button onClick={submit} disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="uw-submit-decision-btn">Submit decision</Button>
        <Button variant="outline" onClick={onGenerateSanction} disabled={busy} data-testid="generate-sanction-btn">Generate sanction letter</Button>
        <Button onClick={onDisburse} disabled={busy} className="bg-teal-600 hover:bg-teal-700" data-testid="disburse-btn">Mark disbursed</Button>
      </div>
    </Card>
  );
}

function Field({ label, value, onChange, type = "text", id }) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} data-testid={`input-${id}`} type={type} value={value ?? ""} onChange={e => onChange(type === "number" ? Number(e.target.value) : e.target.value)} />
    </div>
  );
}

function EmiTab({ app }) {
  const [sched, setSched] = useState(app.emi_schedule || []);
  useEffect(() => {
    if (!sched.length && app.loan_offer?.loan_amount) {
      api.get(`/applications/${app.id}/emi-schedule`).then(r => setSched(r.data.emi_schedule || []));
    }
  // eslint-disable-next-line
  }, []);
  if (!sched.length) return <Card className="p-5 border-slate-200"><div className="text-slate-500 text-sm">No EMI schedule yet. Once disbursed, a schedule will appear here.</div></Card>;
  return (
    <Card className="border-slate-200 overflow-hidden" data-testid="emi-tab">
      <div className="px-5 py-3 border-b border-slate-100 font-semibold text-slate-900 text-sm">EMI Schedule</div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2">#</th>
              <th className="text-left px-4 py-2">Due date</th>
              <th className="text-right px-4 py-2">EMI</th>
              <th className="text-right px-4 py-2">Principal</th>
              <th className="text-right px-4 py-2">Interest</th>
              <th className="text-right px-4 py-2">Balance</th>
              <th className="text-left px-4 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {sched.map(s => (
              <tr key={s.emi_no} className="border-t border-slate-100">
                <td className="px-4 py-2 font-mono">{s.emi_no}</td>
                <td className="px-4 py-2">{s.due_date}</td>
                <td className="px-4 py-2 text-right font-mono">{formatINRDecimal(s.emi)}</td>
                <td className="px-4 py-2 text-right font-mono">{formatINRDecimal(s.principal)}</td>
                <td className="px-4 py-2 text-right font-mono">{formatINRDecimal(s.interest)}</td>
                <td className="px-4 py-2 text-right font-mono">{formatINRDecimal(s.balance)}</td>
                <td className="px-4 py-2"><StatusBadge status={s.status === "paid" ? "Disbursed" : s.status === "bounced" ? "Rejected" : "Documents Pending"} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}


function PartiesTab({ app, onSaved, busyState }) {
  const [busy, setBusy] = busyState;
  const [cb, setCb] = useState({ full_name: "", relationship: "Spouse", mobile: "", email: "", pan: "", dob: "", monthly_income: 0, existing_emi: 0, cibil_score: 720, address: "", employment_type: "Salaried", employer_name: "" });
  const [gu, setGu] = useState({ full_name: "", relationship: "Family Friend", mobile: "", email: "", pan: "", dob: "", monthly_income: 0, existing_emi: 0, cibil_score: 720, address: "", employment_type: "Salaried", employer_name: "" });

  useEffect(() => {
    if (app.coborrower) setCb(c => ({ ...c, ...app.coborrower }));
    if (app.guarantor) setGu(g => ({ ...g, ...app.guarantor }));
  }, [app.id]);

  const save = async (kind, data, setLink) => {
    setBusy(true);
    try {
      const { data: resp } = await api.post(`/applications/${app.id}/${kind}`, { ...data, monthly_income: Number(data.monthly_income), existing_emi: Number(data.existing_emi), cibil_score: Number(data.cibil_score) });
      toast.success(`${kind} saved`);
      navigator.clipboard?.writeText(resp.invite_link || "").catch(() => {});
      toast.message(`Invite link copied to clipboard`, { description: resp.invite_link });
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="parties-tab">
      <PartyCard title="Co-borrower" testId="party-coborrower" form={cb} setForm={setCb} onSave={() => save("coborrower", cb)} busy={busy} existing={app.coborrower} />
      <PartyCard title="Guarantor" testId="party-guarantor" form={gu} setForm={setGu} onSave={() => save("guarantor", gu)} busy={busy} existing={app.guarantor} />
    </div>
  );
}

function PartyCard({ title, testId, form, setForm, onSave, busy, existing }) {
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const F = ({ id, label, type = "text" }) => (
    <div>
      <Label htmlFor={`${testId}-${id}`}>{label}</Label>
      <Input id={`${testId}-${id}`} data-testid={`${testId}-${id}`} type={type} value={form[id] ?? ""} onChange={e => set(id, type === "number" ? Number(e.target.value) : e.target.value)} />
    </div>
  );
  return (
    <Card className="p-5 border-slate-200" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-lg font-semibold text-slate-900">{title}</div>
        {existing?.invite_status && <span className="text-xs px-2 py-0.5 rounded-full badge-progress">Invite {existing.invite_status}</span>}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <F id="full_name" label="Full name" />
        <F id="relationship" label="Relationship" />
        <F id="mobile" label="Mobile" />
        <F id="email" label="Email" />
        <F id="pan" label="PAN" />
        <F id="dob" label="DOB" type="date" />
        <F id="monthly_income" label="Monthly income (₹)" type="number" />
        <F id="existing_emi" label="Existing EMI (₹)" type="number" />
        <F id="cibil_score" label="CIBIL score" type="number" />
        <F id="employer_name" label="Employer" />
      </div>
      <div className="flex justify-end mt-4">
        <Button onClick={onSave} disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid={`${testId}-save-btn`}>Save & generate invite link</Button>
      </div>
    </Card>
  );
}

function ServicingTab({ app, onSaved, busyState }) {
  const [busy, setBusy] = busyState;
  const [enach, setEnach] = useState({ bank_account_no: "", ifsc: "", bank_name: "", account_holder_name: "", max_amount: 50000, frequency: "monthly", status: "initiated", mandate_reference: "", notes: "", ...(app.enach || {}) });
  const [gps, setGps] = useState({ device_id: "", provider: "Generic", install_date: "", status: "pending_install", last_ping: "", last_location: "", notes: "", ...(app.gps || {}) });
  const [rto, setRto] = useState({ rto_state: "", rto_office: "", form_34_filed: false, form_34_date: "", hypothecation_endorsed: false, hypothecation_date: "", noc_status: "not_required", noc_date: "", notes: "", ...(app.rto || {}) });
  const [partners, setPartners] = useState([]);
  const [pools, setPools] = useState([]);
  const [assignCl, setAssignCl] = useState({ partner_id: "", partner_split_pct: 20 });
  const [assignPool, setAssignPool] = useState("");

  useEffect(() => {
    api.get("/admin/colending/partners").then(r => setPartners(r.data)).catch(() => {});
    api.get("/admin/selldown/pools").then(r => setPools(r.data)).catch(() => {});
  }, []);

  const saveEnach = async () => {
    setBusy(true);
    try { await api.post(`/applications/${app.id}/servicing/enach`, { ...enach, max_amount: Number(enach.max_amount) }); toast.success("eNACH saved"); onSaved(); }
    catch { toast.error("Failed"); } finally { setBusy(false); }
  };
  const saveGps = async () => {
    setBusy(true);
    try { await api.post(`/applications/${app.id}/servicing/gps`, gps); toast.success("GPS saved"); onSaved(); }
    catch { toast.error("Failed"); } finally { setBusy(false); }
  };
  const saveRto = async () => {
    setBusy(true);
    try { await api.post(`/applications/${app.id}/servicing/rto`, rto); toast.success("RTO saved"); onSaved(); }
    catch { toast.error("Failed"); } finally { setBusy(false); }
  };
  const assignColending = async () => {
    if (!assignCl.partner_id) return toast.error("Pick a partner");
    setBusy(true);
    try { await api.post(`/applications/${app.id}/colending/assign`, { ...assignCl, partner_split_pct: Number(assignCl.partner_split_pct) }); toast.success("Co-lending allocated"); onSaved(); }
    catch { toast.error("Failed"); } finally { setBusy(false); }
  };
  const assignToPool = async () => {
    if (!assignPool) return toast.error("Pick a pool");
    setBusy(true);
    try { await api.post(`/applications/${app.id}/selldown/assign`, { pool_id: assignPool }); toast.success("Assigned to pool"); onSaved(); }
    catch { toast.error("Failed"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="servicing-tab">
      <Card className="p-5 border-slate-200">
        <div className="text-lg font-semibold text-slate-900 mb-3">eNACH Mandate</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div><Label>Bank account no</Label><Input data-testid="enach-account-no" value={enach.bank_account_no} onChange={e => setEnach(p => ({...p, bank_account_no: e.target.value}))} /></div>
          <div><Label>IFSC</Label><Input data-testid="enach-ifsc" value={enach.ifsc} onChange={e => setEnach(p => ({...p, ifsc: e.target.value}))} /></div>
          <div><Label>Bank name</Label><Input data-testid="enach-bank" value={enach.bank_name} onChange={e => setEnach(p => ({...p, bank_name: e.target.value}))} /></div>
          <div><Label>Account holder</Label><Input data-testid="enach-holder" value={enach.account_holder_name} onChange={e => setEnach(p => ({...p, account_holder_name: e.target.value}))} /></div>
          <div><Label>Max amount (₹)</Label><Input type="number" data-testid="enach-max" value={enach.max_amount} onChange={e => setEnach(p => ({...p, max_amount: e.target.value}))} /></div>
          <div>
            <Label>Status</Label>
            <Select value={enach.status} onValueChange={v => setEnach(p => ({...p, status: v}))}>
              <SelectTrigger data-testid="enach-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="initiated">Initiated</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="failed">Failed</SelectItem><SelectItem value="cancelled">Cancelled</SelectItem></SelectContent>
            </Select>
          </div>
          {enach.mandate_reference && <div className="md:col-span-3 text-xs text-slate-500 font-mono">Reference: {enach.mandate_reference}</div>}
        </div>
        <div className="flex justify-end mt-3"><Button onClick={saveEnach} disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="enach-save-btn">Save eNACH</Button></div>
      </Card>

      <Card className="p-5 border-slate-200">
        <div className="text-lg font-semibold text-slate-900 mb-3">GPS Device</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div><Label>Device ID</Label><Input data-testid="gps-device-id" value={gps.device_id} onChange={e => setGps(p => ({...p, device_id: e.target.value}))} /></div>
          <div><Label>Provider</Label><Input data-testid="gps-provider" value={gps.provider} onChange={e => setGps(p => ({...p, provider: e.target.value}))} /></div>
          <div><Label>Install date</Label><Input type="date" data-testid="gps-install-date" value={gps.install_date || ""} onChange={e => setGps(p => ({...p, install_date: e.target.value}))} /></div>
          <div>
            <Label>Status</Label>
            <Select value={gps.status} onValueChange={v => setGps(p => ({...p, status: v}))}>
              <SelectTrigger data-testid="gps-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pending_install">Pending install</SelectItem>
                <SelectItem value="installed">Installed</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="tamper_detected">Tamper detected</SelectItem>
                <SelectItem value="removed">Removed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Last location</Label><Input data-testid="gps-last-location" value={gps.last_location || ""} onChange={e => setGps(p => ({...p, last_location: e.target.value}))} /></div>
        </div>
        <div className="flex justify-end mt-3"><Button onClick={saveGps} disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="gps-save-btn">Save GPS</Button></div>
      </Card>

      <Card className="p-5 border-slate-200">
        <div className="text-lg font-semibold text-slate-900 mb-3">RTO / Hypothecation</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div><Label>RTO state</Label><Input data-testid="rto-state" value={rto.rto_state || ""} onChange={e => setRto(p => ({...p, rto_state: e.target.value}))} /></div>
          <div><Label>RTO office</Label><Input data-testid="rto-office" value={rto.rto_office || ""} onChange={e => setRto(p => ({...p, rto_office: e.target.value}))} /></div>
          <div>
            <Label>NOC status</Label>
            <Select value={rto.noc_status} onValueChange={v => setRto(p => ({...p, noc_status: v}))}>
              <SelectTrigger data-testid="rto-noc-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="not_required">Not required</SelectItem><SelectItem value="pending">Pending</SelectItem><SelectItem value="issued">Issued</SelectItem></SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm" data-testid="rto-form34">
            <Checkbox checked={!!rto.form_34_filed} onCheckedChange={v => setRto(p => ({...p, form_34_filed: !!v}))} /> Form 34 filed
          </label>
          <div><Label>Form 34 date</Label><Input type="date" data-testid="rto-form34-date" value={rto.form_34_date || ""} onChange={e => setRto(p => ({...p, form_34_date: e.target.value}))} /></div>
          <label className="flex items-center gap-2 text-sm" data-testid="rto-hypo">
            <Checkbox checked={!!rto.hypothecation_endorsed} onCheckedChange={v => setRto(p => ({...p, hypothecation_endorsed: !!v}))} /> Hypothecation endorsed
          </label>
          <div><Label>Hypothecation date</Label><Input type="date" data-testid="rto-hypo-date" value={rto.hypothecation_date || ""} onChange={e => setRto(p => ({...p, hypothecation_date: e.target.value}))} /></div>
        </div>
        <div className="flex justify-end mt-3"><Button onClick={saveRto} disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="rto-save-btn">Save RTO</Button></div>
      </Card>

      <Card className="p-5 border-slate-200">
        <div className="text-lg font-semibold text-slate-900 mb-3">Co-Lending Allocation</div>
        {app.colending ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <KV label="Partner" value={app.colending.partner_name} />
            <KV label="Partner split" value={`${app.colending.partner_split_pct}%`} />
            <KV label="Partner principal" value={formatINR(app.colending.partner_principal)} />
            <KV label="RFC principal" value={formatINR(app.colending.rfc_principal)} />
            <KV label="Partner approval" value={app.colending.partner_approval_status} />
            <KV label="RFC approval" value={app.colending.rfc_approval_status} />
          </div>
        ) : (
          <div className="text-sm text-slate-500 mb-3">No co-lending allocation yet.</div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
          <div>
            <Label>Partner</Label>
            <Select value={assignCl.partner_id} onValueChange={v => setAssignCl(p => ({...p, partner_id: v}))}>
              <SelectTrigger data-testid="colending-partner-select"><SelectValue placeholder="Pick partner" /></SelectTrigger>
              <SelectContent>{partners.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Partner split %</Label><Input type="number" data-testid="colending-split-input" value={assignCl.partner_split_pct} onChange={e => setAssignCl(p => ({...p, partner_split_pct: e.target.value}))} /></div>
          <div className="flex items-end"><Button onClick={assignColending} disabled={busy} className="bg-teal-600 hover:bg-teal-700 w-full" data-testid="colending-assign-btn">Allocate</Button></div>
        </div>
      </Card>

      <Card className="p-5 border-slate-200">
        <div className="text-lg font-semibold text-slate-900 mb-3">Sell-Down Pool</div>
        {app.selldown_pool_name ? (
          <div className="text-sm mb-3">Assigned to: <span className="font-mono text-teal-700">{app.selldown_pool_name}</span> ({(app.selldown_assigned_at || "").slice(0, 10)})</div>
        ) : (
          <div className="text-sm text-slate-500 mb-3">Not assigned to any pool.</div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <Label>Pool</Label>
            <Select value={assignPool} onValueChange={setAssignPool}>
              <SelectTrigger data-testid="selldown-pool-select"><SelectValue placeholder="Pick pool" /></SelectTrigger>
              <SelectContent>{pools.map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({p.pool_type})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-end"><Button onClick={assignToPool} disabled={busy} className="bg-teal-600 hover:bg-teal-700 w-full" data-testid="selldown-assign-btn">Assign to pool</Button></div>
        </div>
      </Card>
    </div>
  );
}

function BureauAATab({ app, onSaved, busyState }) {
  const [busy, setBusy] = busyState;
  const [subject, setSubject] = useState("borrower");
  const bureauKey = `bureau_${subject}`;
  const aaKey = `aa_${subject}`;
  const consents = app.consents || {};
  const bureau = (app.bureau || {})[subject];
  const aa = (app.aa || {})[subject];
  const hasCob = !!app.coborrower?.full_name;

  const captureBureauConsent = async () => {
    setBusy(true);
    try { await api.post(`/applications/${app.id}/bureau/consent`, { subject, purpose: "loan_origination", captured_via: "web" }); toast.success("Bureau consent captured"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const pullBureau = async () => {
    setBusy(true);
    try { await api.post(`/applications/${app.id}/bureau/pull?subject=${subject}`); toast.success("CIBIL pulled"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const captureAAConsent = async () => {
    setBusy(true);
    try { await api.post(`/applications/${app.id}/aa/consent`, { subject, purpose: "loan_origination", fip_list: ["HDFC Bank", "SBI", "ICICI Bank"], data_types: ["DEPOSIT"], months: 6 }); toast.success("AA consent captured"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const fetchAA = async () => {
    setBusy(true);
    try { await api.post(`/applications/${app.id}/aa/fetch?subject=${subject}`); toast.success("AA statement fetched"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="bureau-aa-tab">
      <div className="p-3 rounded-md bg-blue-50 border border-blue-200 text-blue-900 text-xs flex items-start gap-2" data-testid="auto-bureau-placeholder">
        <Info size={14} className="mt-0.5 shrink-0" />
        <div>
          <span className="font-semibold">Auto bureau pull on submission</span> — Coming soon. CIBIL pull will fire automatically once the borrower submits, provided consent is captured. For now, capture consent and pull manually below.
        </div>
      </div>
      <Card className="p-4 border-slate-200 flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-900">Subject</div>
          <div className="text-xs text-slate-500">Choose whose data to fetch.</div>
        </div>
        <div className="flex gap-2">
          {["borrower", ...(hasCob ? ["coborrower"] : [])].map(s => (
            <button
              key={s}
              data-testid={`subject-${s}`}
              onClick={() => setSubject(s)}
              className={`px-4 py-2 rounded-md text-sm font-medium capitalize border transition-colors ${subject === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-teal-300"}`}
            >{s}</button>
          ))}
        </div>
      </Card>

      <Card className="p-5 border-slate-200" data-testid="bureau-card">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="text-lg font-semibold text-slate-900">CIBIL / Credit Bureau</div>
            <div className="text-sm text-slate-600">Pull credit score and tradelines for the selected subject.</div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={captureBureauConsent} disabled={busy} data-testid="bureau-consent-btn">{consents[bureauKey]?.status === "active" ? "Re-capture consent" : "Capture consent"}</Button>
            <Button onClick={pullBureau} disabled={busy || consents[bureauKey]?.status !== "active"} className="bg-teal-600 hover:bg-teal-700" data-testid="bureau-pull-btn">Pull CIBIL</Button>
          </div>
        </div>
        {consents[bureauKey] && (
          <div className="text-xs text-slate-500 mb-3 font-mono">Consent: {consents[bureauKey].consent_ref} · {consents[bureauKey].status}</div>
        )}
        {bureau ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KV label="Score" value={bureau.score} highlight />
              <KV label="Band" value={bureau.score_band} />
              <KV label="Provider" value={bureau.provider} />
              <KV label="As of" value={(bureau.as_of || "").slice(0, 10)} />
              <KV label="Active accounts" value={bureau.summary?.active_accounts} />
              <KV label="Closed accounts" value={bureau.summary?.closed_accounts} />
              <KV label="Overdue" value={bureau.summary?.overdue_accounts} />
              <KV label="Active EMI" value={formatINR(bureau.summary?.total_active_emi)} />
              <KV label="Outstanding" value={formatINR(bureau.summary?.total_outstanding)} />
              <KV label="Enquiries 6m" value={bureau.summary?.enquiries_last_6m} />
              <KV label="Oldest account" value={`${bureau.summary?.oldest_account_months || 0} mo`} />
            </div>
            <div className="mt-5">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Tradelines</div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                  <tr><th className="text-left px-3 py-2">Lender</th><th className="text-left px-3 py-2">Type</th><th className="text-right px-3 py-2">Sanctioned</th><th className="text-right px-3 py-2">Balance</th><th className="text-right px-3 py-2">EMI</th><th className="text-left px-3 py-2">Opened</th><th className="text-left px-3 py-2">Status</th></tr>
                </thead>
                <tbody>
                  {(bureau.tradelines || []).map((t, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-3 py-2">{t.lender}</td>
                      <td className="px-3 py-2">{t.account_type}</td>
                      <td className="px-3 py-2 text-right font-mono">{formatINR(t.sanctioned_amount)}</td>
                      <td className="px-3 py-2 text-right font-mono">{formatINR(t.current_balance)}</td>
                      <td className="px-3 py-2 text-right font-mono">{formatINR(t.emi)}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{t.opened_on}</td>
                      <td className="px-3 py-2"><span className={"px-2 py-0.5 rounded-full text-xs " + (t.status === "Active" ? "badge-progress" : t.status === "Closed" ? "badge-neutral" : "badge-rejected")}>{t.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="text-sm text-slate-500">No bureau pulled yet for <span className="font-medium capitalize">{subject}</span>.</div>
        )}
      </Card>

      <Card className="p-5 border-slate-200" data-testid="aa-card">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="text-lg font-semibold text-slate-900">Account Aggregator (Bank Statement)</div>
            <div className="text-sm text-slate-600">Pull last 6 months of bank statement via the RBI-regulated AA framework.</div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={captureAAConsent} disabled={busy} data-testid="aa-consent-btn">{consents[aaKey]?.status === "active" ? "Re-capture consent" : "Capture consent"}</Button>
            <Button onClick={fetchAA} disabled={busy || consents[aaKey]?.status !== "active"} className="bg-teal-600 hover:bg-teal-700" data-testid="aa-fetch-btn">Fetch statement</Button>
          </div>
        </div>
        {consents[aaKey] && (
          <div className="text-xs text-slate-500 mb-3 font-mono">Consent: {consents[aaKey].consent_ref} · {consents[aaKey].status}</div>
        )}
        {aa ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KV label="Bank" value={`${aa.account?.bank} ${aa.account?.masked_no}`} />
              <KV label="IFSC" value={aa.account?.ifsc} />
              <KV label="Period" value={`${aa.period_months} months`} />
              <KV label="Provider" value={aa.provider} />
              <KV label="Salary credits" value={aa.summary?.salary_credit_count} />
              <KV label="Inferred salary" value={formatINR(aa.summary?.salary_inferred_monthly)} highlight />
              <KV label="Average balance" value={formatINR(aa.summary?.average_balance)} />
              <KV label="Inferred EMI" value={formatINR(aa.summary?.inferred_emi_obligations)} />
              <KV label="Total credits" value={formatINR(aa.summary?.total_credits)} />
              <KV label="Total debits" value={formatINR(aa.summary?.total_debits)} />
              <KV label="Net inflow" value={formatINR(aa.summary?.net_inflow)} />
              <KV label="Transactions" value={aa.summary?.transaction_count} />
            </div>
            <div className="mt-5">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Recent transactions (top 20)</div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                  <tr><th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Narration</th><th className="text-right px-3 py-2">Credit</th><th className="text-right px-3 py-2">Debit</th></tr>
                </thead>
                <tbody>
                  {(aa.transactions || []).slice(0, 20).map((t, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-3 py-2 text-xs font-mono">{t.date}</td>
                      <td className="px-3 py-2 text-xs">{t.narration}</td>
                      <td className="px-3 py-2 text-right font-mono text-teal-700">{t.credit ? formatINR(t.credit) : ""}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-700">{t.debit ? formatINR(t.debit) : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="text-sm text-slate-500">No AA statement fetched yet for <span className="font-medium capitalize">{subject}</span>.</div>
        )}
      </Card>
    </div>
  );
}



// ---------------- Valuation Breakdown (Waterfall) ----------------
function ValuationBreakdown({ v }) {
  const base = Number(v.market_value || 0);
  const agePct = Number(v.age_discount_pct || 0);
  const kmPct = Number(v.km_discount_pct || 0);
  const diagPct = Number(v.diagnostic_adjustment_pct || 0);
  const repair = Number(v.repair_adjustment || 0);
  const liquidityPct = 5; // matches business_logic.liquidity_discount = 0.05
  const ageAmt = base * agePct / 100;
  const kmAmt = base * kmPct / 100;
  const diagAmt = base * diagPct / 100;
  const liqAmt = base * liquidityPct / 100;
  const lendable = Number(v.lendable_value || 0);
  const maxLtvPct = Math.round((Number(v.recommended_max_ltv) || 0) * 100);
  const recLoan = Number(v.recommended_loan_amount || 0);

  return (
    <div className="space-y-5" data-testid="valuation-breakdown">
      <div className="rounded-lg border border-slate-200 overflow-hidden">
        <div className="bg-slate-50 px-4 py-2.5 text-xs uppercase tracking-wider text-slate-600 font-semibold flex items-center gap-2">
          <Sparkles size={14} className="text-teal-600" /> Lendable Value — step-by-step breakdown
        </div>
        <div className="divide-y divide-slate-100">
          <WRow label="Base market value" sublabel="CarFin internal IDV" value={`+ ${formatINR(base)}`} kind="base" />
          <WRow label={`Age discount`} sublabel={`${agePct}% · vehicle age decay`} value={`− ${formatINR(ageAmt)}`} kind="neg" />
          <WRow label={`Odometer discount`} sublabel={`${kmPct}% · km driven impact`} value={`− ${formatINR(kmAmt)}`} kind="neg" />
          <WRow label={`Diagnostic adjustment`} sublabel={`${diagPct}% · CarDr health grade ${v.health_grade || "—"}`} value={`− ${formatINR(diagAmt)}`} kind="neg" />
          <WRow label="Repair estimate" sublabel="CarDr repair cost line items" value={`− ${formatINR(repair)}`} kind="neg" />
          <WRow label="Liquidity discount" sublabel={`${liquidityPct}% · resale haircut`} value={`− ${formatINR(liqAmt)}`} kind="neg" />
          <WRow label="Lendable value" sublabel="Post-adjustment collateral value" value={`= ${formatINR(lendable)}`} kind="total" />
          <WRow label={`× Max LTV cap`} sublabel={`Policy LTV for grade ${v.health_grade || "—"}`} value={`${maxLtvPct}%`} kind="op" />
          <WRow label="Recommended loan amount" sublabel="lendable_value × max_ltv" value={formatINR(recLoan)} kind="final" />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
        <KV label="Retail value" value={formatINR(v.retail_value)} />
        <KV label="Dealer value" value={formatINR(v.dealer_value)} />
        <KV label="Wholesale value" value={formatINR(v.wholesale_value)} />
        <KV label="Distress value" value={formatINR(v.distress_value)} />
        <KV label="Confidence" value={`${v.confidence_score}/100`} />
      </div>

      {v.flags?.length > 0 && (
        <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-sm" data-testid="valuation-flags">
          <div className="font-semibold mb-1 flex items-center gap-1.5"><AlertTriangle size={14} /> Underwriting flags raised by valuation</div>
          <ul className="list-disc pl-5 space-y-1">{v.flags.map((f, i) => <li key={i}>{f}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

function WRow({ label, sublabel, value, kind }) {
  const styles = {
    base: { row: "bg-white", val: "text-slate-900 font-semibold", icon: null },
    neg: { row: "bg-white", val: "text-rose-700 font-semibold", icon: <ArrowDown size={14} className="text-rose-500" /> },
    op: { row: "bg-slate-50/60", val: "text-slate-700 font-semibold", icon: null },
    total: { row: "bg-teal-50/70", val: "text-teal-800 font-bold", icon: null },
    final: { row: "bg-slate-900", val: "text-teal-200 font-bold", icon: null, dark: true },
  }[kind] || { row: "bg-white", val: "text-slate-900", icon: null };
  return (
    <div className={`px-4 py-3 flex items-center justify-between ${styles.row}`} data-testid={`wrow-${kind}`}>
      <div className="flex items-start gap-2">
        {styles.icon}
        <div>
          <div className={`text-sm ${styles.dark ? "text-white" : "text-slate-900"} font-medium`}>{label}</div>
          {sublabel && <div className={`text-xs ${styles.dark ? "text-slate-400" : "text-slate-500"}`}>{sublabel}</div>}
        </div>
      </div>
      <div className={`font-mono text-sm ${styles.val}`}>{value}</div>
    </div>
  );
}

// ---------------- Risk Flag Panel (Underwriter) ----------------
function RiskFlagPanel({ app }) {
  const chips = deriveRiskChips(app);
  const counts = chips.reduce((acc, c) => { acc[c.severity] = (acc[c.severity] || 0) + 1; return acc; }, {});
  return (
    <Card className="p-5 border-slate-200" data-testid="risk-flag-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ShieldAlert size={18} className="text-slate-700" />
          <div>
            <div className="text-lg font-semibold text-slate-900">Risk Flags & Reason Codes</div>
            <div className="text-sm text-slate-600">Aggregated from CarDr, valuation, bureau, and policy engine.</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-xs">
          <span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-800 font-semibold" data-testid="risk-count-high">{counts.high || 0} High</span>
          <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold" data-testid="risk-count-medium">{counts.medium || 0} Medium</span>
          <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 font-semibold" data-testid="risk-count-info">{counts.info || 0} Info</span>
        </div>
      </div>
      {chips.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2" data-testid="risk-clean">
          <CheckCircle2 size={16} /> No risk flags detected — clean file.
        </div>
      ) : (
        <div className="flex flex-wrap gap-2" data-testid="risk-chip-list">
          {chips.map((c, i) => <Chip key={i} {...c} idx={i} />)}
        </div>
      )}
    </Card>
  );
}

function Chip({ severity, code, label, idx }) {
  const palette = {
    high: "bg-rose-50 border-rose-200 text-rose-800",
    medium: "bg-amber-50 border-amber-200 text-amber-800",
    info: "bg-blue-50 border-blue-200 text-blue-800",
  }[severity];
  const Icon = severity === "high" ? AlertTriangle : severity === "medium" ? AlertTriangle : Info;
  return (
    <div className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full border ${palette}`} data-testid={`risk-chip-${idx}`} title={code}>
      <Icon size={12} />
      <span className="font-mono text-[10px] uppercase tracking-wider opacity-70">{code}</span>
      <span className="font-medium">{label}</span>
    </div>
  );
}

function deriveRiskChips(app) {
  const chips = [];
  const b = app.borrower || {};
  const v = app.vehicle || {};
  const cd = app.cardr || {};
  const val = app.valuation || {};
  const cr = app.credit_decision || {};
  const bur = (app.bureau || {}).borrower || null;

  // CIBIL
  const cibil = Number(b.cibil_score || bur?.score || 0);
  if (cibil > 0 && cibil < RISK_THRESHOLDS.CIBIL_MIN) chips.push({ severity: "high", code: "CIBIL-LOW", label: `CIBIL ${cibil} (< ${RISK_THRESHOLDS.CIBIL_MIN})` });
  else if (cibil >= RISK_THRESHOLDS.CIBIL_MIN && cibil < RISK_THRESHOLDS.CIBIL_SUBPRIME) chips.push({ severity: "medium", code: "CIBIL-SUB", label: `Sub-prime CIBIL ${cibil}` });

  // FOIR
  if (cr.foir != null) {
    const foirPct = Math.round((cr.foir || 0) * 1000) / 10;
    if (foirPct > RISK_THRESHOLDS.FOIR_HIGH_PCT) chips.push({ severity: "high", code: "FOIR-HIGH", label: `FOIR ${foirPct}% (> ${RISK_THRESHOLDS.FOIR_HIGH_PCT}%)` });
    else if (foirPct > RISK_THRESHOLDS.FOIR_WATCH_PCT) chips.push({ severity: "medium", code: "FOIR-WATCH", label: `FOIR ${foirPct}%` });
  }

  // LTV
  if (cr.ltv != null) {
    const ltvPct = Math.round((cr.ltv || 0) * 1000) / 10;
    if (ltvPct > RISK_THRESHOLDS.LTV_HIGH_PCT) chips.push({ severity: "high", code: "LTV-OVER", label: `LTV ${ltvPct}% (> ${RISK_THRESHOLDS.LTV_HIGH_PCT}%)` });
  }

  // Vehicle age & KM
  const year = Number(v.year || 0);
  const age = year ? (new Date().getFullYear() - year) : 0;
  if (age >= RISK_THRESHOLDS.VEHICLE_AGE_MAX) chips.push({ severity: "high", code: "AGE-MAX", label: `Vehicle ${age}y old (≥ ${RISK_THRESHOLDS.VEHICLE_AGE_MAX}y)` });
  else if (age >= RISK_THRESHOLDS.VEHICLE_AGE_WATCH) chips.push({ severity: "medium", code: "AGE-WATCH", label: `Vehicle ${age}y old` });

  const km = Number(v.km_driven || 0);
  if (km > RISK_THRESHOLDS.KM_MAX) chips.push({ severity: "high", code: "KM-OVER", label: `Odometer ${km.toLocaleString("en-IN")} km` });
  else if (km > RISK_THRESHOLDS.KM_WATCH) chips.push({ severity: "medium", code: "KM-WATCH", label: `Odometer ${km.toLocaleString("en-IN")} km` });

  // CarDr flags
  if (cd.vin_mismatch_flag) chips.push({ severity: "high", code: "VIN-MISMATCH", label: "VIN mismatch on CarDr" });
  if (cd.mileage_mismatch_flag) chips.push({ severity: "high", code: "ODO-MISMATCH", label: "Odometer mismatch on CarDr" });
  if (cd.code_reset_detected) chips.push({ severity: "medium", code: "CODE-RESET", label: "ECU code reset detected" });
  if (cd.underwriting_warning_flag) chips.push({ severity: "high", code: "UW-WARN", label: "CarDr underwriting warning" });
  if (cd.recommended_inspection_flag) chips.push({ severity: "medium", code: "INSPECT-REC", label: "Manual inspection recommended" });

  // Health grade
  const grade = cd.health_grade || val.health_grade;
  if (grade === "D") chips.push({ severity: "high", code: "GRADE-D", label: "Vehicle health grade D" });
  else if (grade === "Reject") chips.push({ severity: "high", code: "GRADE-REJ", label: "Vehicle graded Reject" });
  else if (grade === "C") chips.push({ severity: "medium", code: "GRADE-C", label: "Vehicle health grade C" });

  // Repair exposure
  const market = Number(val.market_value || v.market_value || v.vehicle_price || 0);
  const repair = Number(val.repair_adjustment || cd.repair_estimate || 0);
  if (market > 0 && repair / market > RISK_THRESHOLDS.REPAIR_TO_VALUE_PCT) {
    chips.push({ severity: "high", code: "REPAIR-HIGH", label: `Repair > ${Math.round(RISK_THRESHOLDS.REPAIR_TO_VALUE_PCT * 100)}% of value (${formatINR(repair)})` });
  }

  // Confidence
  if (val.confidence_score != null && val.confidence_score < RISK_THRESHOLDS.CONFIDENCE_LOW) {
    chips.push({ severity: "medium", code: "CONF-LOW", label: `Valuation confidence ${val.confidence_score}/100` });
  }

  // Bureau-derived
  if (bur?.summary?.overdue_accounts > 0) {
    chips.push({ severity: "high", code: "OVERDUE-TL", label: `${bur.summary.overdue_accounts} overdue tradeline(s)` });
  }
  if (bur?.summary?.enquiries_last_6m > RISK_THRESHOLDS.ENQUIRIES_6M_HIGH) {
    chips.push({ severity: "medium", code: "ENQ-HIGH", label: `${bur.summary.enquiries_last_6m} enquiries in last 6m` });
  }

  // Reason codes from credit engine — info chips
  (cr.reason_codes || []).forEach((rc, i) => {
    chips.push({ severity: "info", code: `RC-${i + 1}`, label: rc });
  });
  // Valuation flags — info chips (medium since they came from engine)
  (val.flags || []).forEach((f, i) => {
    chips.push({ severity: "medium", code: `VAL-${i + 1}`, label: f });
  });

  // Dedupe by label (case-insensitive) — keep first occurrence
  const seen = new Set();
  return chips.filter(c => {
    const key = (c.label || "").trim().toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}


// ---------------- Currency Conversion Banner (CarDr tab) ----------------
function CurrencyConversionBanner({ cardr }) {
  if (!cardr || !cardr.source_currency || cardr.source_currency === "INR") return null;
  const fx = cardr.fx_rate_used;
  const unit = cardr.source_mileage_unit === "mi" ? "miles → km" : null;
  return (
    <div className="mb-4 p-3 rounded-md bg-indigo-50 border border-indigo-200 text-indigo-900 text-sm flex items-start gap-2" data-testid="currency-conversion-banner">
      <Info size={16} className="mt-0.5 shrink-0" />
      <div className="flex-1">
        <div className="font-semibold">CarDr report converted from {cardr.source_currency} ({cardr.source_region || "—"})</div>
        <div className="text-xs mt-1 space-x-3">
          {fx && <span>FX rate applied: <span className="font-mono">1 {cardr.source_currency} = ₹{Number(fx).toFixed(2)}</span></span>}
          {unit && <span>Odometer converted: <span className="font-mono">{cardr.source_odometer_raw?.toLocaleString("en-US")} {unit}</span></span>}
          {cardr.source_repair_raw != null && <span>Repair: <span className="font-mono">{cardr.source_currency} {Number(cardr.source_repair_raw).toLocaleString("en-US")} → ₹{Number(cardr.repair_estimate).toLocaleString("en-IN")}</span></span>}
        </div>
      </div>
    </div>
  );
}

// ---------------- Market Estimate (Spinny / Cars24 / CarAndBike via Gemini) ----------------
function MarketEstimateCard({ app }) {
  const [busy, setBusy] = useState(false);
  const [estimate, setEstimate] = useState(app.market_estimate || null);
  const v = app.vehicle || {};
  const ready = !!(v.make && v.model && v.year);

  const fetchEstimate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/valuation/market-estimate", {
        make: v.make,
        model: v.model,
        year: Number(v.year) || 0,
        km_driven: Number(v.km_driven) || 0,
        variant: v.variant || "",
        fuel_type: v.fuel_type || "Petrol",
        ownership_count: Number(v.ownership_count) || 1,
        city: app.borrower?.city || "Delhi",
        health_grade: app.cardr?.health_grade || app.valuation?.health_grade || "B",
      }, { timeout: 90000 });
      setEstimate(data);
      // Persist back to app so it survives reloads + auto-wires vehicle.market_value + re-runs valuation server-side
      try { await api.patch(`/applications/${app.id}/market-estimate`, data); } catch (persistErr) { console.warn("Could not persist market estimate:", persistErr?.response?.data?.detail || persistErr.message); }
      toast.success("Market estimate refreshed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Market estimate failed");
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-5 border-slate-200" data-testid="market-estimate-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-lg font-semibold text-slate-900 flex items-center gap-2"><Sparkles size={18} className="text-teal-600" /> Market Value Range</div>
          <div className="text-sm text-slate-600">Indicative INR range derived from Spinny / Cars24 / CarAndBike-style listings.</div>
        </div>
        <Button onClick={fetchEstimate} disabled={busy || !ready} className="bg-slate-900 hover:bg-slate-800" data-testid="market-estimate-fetch-btn">
          {busy ? "Estimating…" : estimate ? "Refresh estimate" : "Get market estimate"}
        </Button>
      </div>
      {!ready ? (
        <div className="text-sm text-slate-500">Make, model and year are required before generating a market estimate.</div>
      ) : !estimate ? (
        <div className="text-sm text-slate-500">No estimate generated yet. Click <span className="font-medium">Get market estimate</span> above.</div>
      ) : (
        <div className="space-y-3" data-testid="market-estimate-result">
          <div className="grid grid-cols-3 gap-3">
            <RangeStat label="Low" value={estimate.low_inr} tone="rose" />
            <RangeStat label="Mid" value={estimate.mid_inr} tone="teal" highlight />
            <RangeStat label="High" value={estimate.high_inr} tone="emerald" />
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span className="px-2 py-0.5 rounded-full bg-slate-100 font-mono uppercase tracking-wider">{estimate.confidence || "medium"} confidence</span>
            <span>Sources: {(estimate.sources || []).join(" · ")}</span>
          </div>
          {estimate.source_notes && <div className="text-sm text-slate-700">{estimate.source_notes}</div>}
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            ⚠ {estimate.knowledge_cutoff_disclaimer}
          </div>
        </div>
      )}
    </Card>
  );
}

function RangeStat({ label, value, tone = "slate", highlight }) {
  const toneClass = {
    rose: "border-rose-200 bg-rose-50 text-rose-900",
    teal: "border-teal-200 bg-teal-50 text-teal-900",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
    slate: "border-slate-200 bg-slate-50 text-slate-900",
  }[tone] || "border-slate-200 bg-slate-50 text-slate-900";
  return (
    <div className={`rounded-lg border ${toneClass} px-4 py-3`} data-testid={`market-stat-${label.toLowerCase()}`}>
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-80">{label}</div>
      <div className={`mt-1 font-mono ${highlight ? "text-2xl font-bold" : "text-xl font-semibold"}`}>{formatINR(value)}</div>
    </div>
  );
}
